import React from "react";

// Routing
import {useHistory} from "react-router-dom";

// Utils
import moment from "moment-timezone";

// Components
import MaterialTable from "material-table";
import Grid from "@material-ui/core/Grid";
import Chip from "@material-ui/core/Chip";
import SectionTitle, {ActionButton} from "./SectionTitle";
import Box from "@material-ui/core/Box";

// Icons
import tableIcons from "../../Table/TableIcons";
import VisibilityIcon from "@material-ui/icons/Visibility";
import ReplayIcon from "@material-ui/icons/Replay";

const currencyFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const STATUS_COLORS = {
  Succeeded: "#19a86d",
  "Partially refunded": "#dfad1d",
  Refunded: "#de4141",
  Failed: "#de4141",
};

// Status and refund state in one label
const getStatusLabel = transaction => {
  if (transaction.refunded) return "Refunded";
  if (Number(transaction.amount_refunded) > 0) return "Partially refunded";
  return transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1);
};

// What's left to refund on a charge, in dollars
export const getRefundableAmount = transaction =>
  Math.max(
    0,
    Math.round(
      (Number(transaction.amount) - Number(transaction.amount_refunded)) * 100
    ) / 100
  );

const TransactionsTableComponent = ({
  transactionArray,
  isLoading,
  headerAction,
  onRefund,
}) => {
  // Routing
  const history = useHistory();

  // Need to spread array so it doesn't fail
  const formattedData = transactionArray.map(o => ({...o}));

  return (
    <Grid container item xs={12}>
      <Grid item xs={12}>
        <MaterialTable
          title={<SectionTitle title="Transactions" action={headerAction} />}
          isLoading={isLoading}
          icons={tableIcons}
          columns={[
            {
              title: "Date",
              field: "created",
              render: rowData => (
                <>
                  {moment
                    .unix(rowData.created)
                    .tz("America/Chicago")
                    .format("lll")}
                </>
              ),
            },
            {
              title: "Amount",
              field: "amount",
              type: "currency",
              headerStyle: {
                textAlign: "right",
              },
            },
            {
              title: "Refunded",
              field: "amount_refunded",
              type: "currency",
              headerStyle: {
                textAlign: "right",
              },
              render: rowData =>
                Number(rowData.amount_refunded) > 0 ? (
                  <span style={{color: STATUS_COLORS.Refunded}}>
                    –{currencyFormat.format(rowData.amount_refunded)}
                  </span>
                ) : (
                  "—"
                ),
            },
            {
              title: "Status",
              field: "status",
              render: rowData => {
                const label = getStatusLabel(rowData);
                const color = STATUS_COLORS[label];

                return (
                  <Chip
                    size="small"
                    label={label}
                    variant="outlined"
                    style={color ? {color, borderColor: color} : undefined}
                  />
                );
              },
            },
            {
              title: "Actions",
              field: "transaction_id",
              sorting: false,
              align: "right",
              render: rowData => {
                const canRefund =
                  rowData.status === "succeeded" &&
                  getRefundableAmount(rowData) > 0;

                return (
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    style={{gap: 8}}>
                    <ActionButton
                      tone="neutral"
                      icon={<VisibilityIcon />}
                      onClick={() =>
                        history.push(`/sales/charges/${rowData.transaction_id}`)
                      }>
                      Details
                    </ActionButton>
                    {onRefund && (
                      <ActionButton
                        tone="danger"
                        icon={<ReplayIcon />}
                        disabled={!canRefund}
                        onClick={() => onRefund(rowData)}>
                        Refund
                      </ActionButton>
                    )}
                  </Box>
                );
              },
            },
          ]}
          data={formattedData}
          options={{
            search: false,
            paging: false,
          }}
          localization={{
            body: {emptyDataSourceMessage: "No charges on this order yet."},
          }}
        />
      </Grid>
    </Grid>
  );
};

export default TransactionsTableComponent;
